import { z } from 'zod';
import { CommsChannel } from '../enums.js';
import { IsoDate, IsoDateTime, Pence, Uuid } from '../primitives.js';

/**
 * Reporting contract (Phase 4). Read-only analytics; NO tables, NO migration. Response schemas
 * carry `.meta({ id })`; query schemas are INLINE in the route file (NOT here) and must NOT carry
 * `.meta` — so this file exports only the response/entity schemas and the local enum.
 * Money is integer pence; ratios are integer basis points (no floats), consistent with pence policy.
 */

// ── Enum ─────────────────────────────────────────────────────────────────────
export const ReportNoShowRateSource = z.enum(['computed', 'default', 'override']);
export type ReportNoShowRateSource = z.infer<typeof ReportNoShowRateSource>;

// ── Shared bucket ──────────────────────────────────────────────────────────────
export const RevenueBucket = z
  .object({
    key: z.string().describe("ISO week 'YYYY-Www', month 'YYYY-MM' or day 'YYYY-MM-DD'"),
    periodStart: IsoDate,
    periodEnd: IsoDate,
    grossPence: Pence,
    expectedPence: Pence.describe('gross after no-show discount; projection only'),
    appointmentCount: z.int().min(0),
  })
  .meta({ id: 'RevenueBucket' });
export type RevenueBucket = z.infer<typeof RevenueBucket>;

// ── Future-revenue projection (hero report) ────────────────────────────────────
export const FutureRevenueProjection = z
  .object({
    from: IsoDate,
    horizonWeeks: z.int(),
    noShowRate: z.number().min(0).max(1),
    noShowRateSource: ReportNoShowRateSource,
    grossPence: Pence,
    expectedPence: Pence,
    byWeek: z.array(RevenueBucket),
    byMonth: z.array(RevenueBucket),
    generatedAt: IsoDateTime,
  })
  .meta({ id: 'FutureRevenueProjection' });
export type FutureRevenueProjection = z.infer<typeof FutureRevenueProjection>;

// ── Takings ────────────────────────────────────────────────────────────────────
export const TakingsRow = z
  .object({
    key: z.string().describe('date / staffId / serviceId'),
    label: z.string(),
    grossPence: Pence,
    netPence: Pence,
    refundedPence: Pence,
    paymentCount: z.int().min(0),
    tipPence: Pence,
  })
  .meta({ id: 'TakingsRow' });
export type TakingsRow = z.infer<typeof TakingsRow>;

export const TakingsReport = z
  .object({
    from: IsoDate,
    to: IsoDate,
    groupBy: z.enum(['day', 'staff', 'service']),
    totalGrossPence: Pence,
    totalNetPence: Pence,
    totalRefundedPence: Pence,
    rows: z.array(TakingsRow),
    generatedAt: IsoDateTime,
  })
  .meta({ id: 'TakingsReport' });
export type TakingsReport = z.infer<typeof TakingsReport>;

// ── Comms cost ─────────────────────────────────────────────────────────────────
export const CommsChannelCost = z
  .object({
    channel: CommsChannel,
    messages: z.int().min(0),
    segments: z.int().min(0),
    costPence: Pence,
    savedPence: Pence,
  })
  .meta({ id: 'CommsChannelCost' });
export type CommsChannelCost = z.infer<typeof CommsChannelCost>;

export const CommsCostReport = z
  .object({
    from: IsoDate,
    to: IsoDate,
    totalCostPence: Pence,
    totalMessages: z.int().min(0),
    smartRoutingSavedPence: Pence,
    byChannel: z.array(CommsChannelCost),
    byDay: z.array(z.object({ date: IsoDate, costPence: Pence, messages: z.int() })),
  })
  .meta({ id: 'CommsCostReport' });
export type CommsCostReport = z.infer<typeof CommsCostReport>;

// ── Occupancy ──────────────────────────────────────────────────────────────────
export const StaffOccupancy = z
  .object({
    staffId: Uuid,
    staffName: z.string(),
    availableMinutes: z.int().min(0),
    bookedMinutes: z.int().min(0),
    utilisationBps: z.int().min(0).max(10000),
  })
  .meta({ id: 'StaffOccupancy' });
export type StaffOccupancy = z.infer<typeof StaffOccupancy>;

export const OccupancyReport = z
  .object({
    from: IsoDate,
    to: IsoDate,
    availableChairMinutes: z.int().min(0),
    bookedChairMinutes: z.int().min(0),
    utilisationBps: z.int().min(0).max(10000),
    byStaff: z.array(StaffOccupancy),
    byDay: z.array(
      z.object({
        date: IsoDate,
        availableMin: z.int(),
        bookedMin: z.int(),
        utilisationBps: z.int(),
      }),
    ),
  })
  .meta({ id: 'OccupancyReport' });
export type OccupancyReport = z.infer<typeof OccupancyReport>;

// ── Staff KPI ──────────────────────────────────────────────────────────────────
export const StaffKpiRow = z
  .object({
    staffId: Uuid,
    staffName: z.string(),
    completedAppointments: z.int().min(0),
    noShows: z.int().min(0),
    cancellations: z.int().min(0),
    noShowRateBps: z.int().min(0).max(10000),
    revenuePence: Pence,
    averageTicketPence: Pence,
    rebookRateBps: z.int().min(0).max(10000),
    utilisationBps: z.int().min(0).max(10000),
    newClients: z.int().min(0),
    /**
     * WS9 — commission/earnings columns (feed the WS4 mobile KPI screen). `commissionPence` is the
     * commission computed by the staff member's rules over RECOGNISED revenue (real takings + voucher
     * REDEMPTION revenue — NEVER voucher sales); `earningsPence` is what they earned this period.
     */
    commissionPence: Pence,
    earningsPence: Pence,
  })
  .meta({ id: 'StaffKpiRow' });
export type StaffKpiRow = z.infer<typeof StaffKpiRow>;

export const StaffKpiDashboard = z
  .object({
    from: IsoDate,
    to: IsoDate,
    rows: z.array(StaffKpiRow),
    generatedAt: IsoDateTime,
  })
  .meta({ id: 'StaffKpiDashboard' });
export type StaffKpiDashboard = z.infer<typeof StaffKpiDashboard>;

// ── Payment cost composition (Overview 6.10) ────────────────────────────────────
export const PaymentCostComposition = z
  .object({
    from: IsoDate,
    to: IsoDate,
    grossPence: Pence,
    platformFeePence: Pence,
    stripeFeePence: Pence,
    netToSalonPence: Pence,
    effectiveRateBps: z.int().min(0),
    byMethod: z.array(
      z.object({
        method: z.string(),
        grossPence: Pence,
        feePence: Pence,
        count: z.int(),
      }),
    ),
  })
  .meta({ id: 'PaymentCostComposition' });
export type PaymentCostComposition = z.infer<typeof PaymentCostComposition>;
